import { directoryCategories } from '../../../mocks/directory';

interface DirectoryFiltersProps {
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
  minRating: number;
  onMinRatingChange: (r: number) => void;
  verifiedOnly: boolean;
  onVerifiedChange: (v: boolean) => void;
  sortBy: string;
  onSortChange: (s: string) => void;
  onClearAll: () => void;
}

export default function DirectoryFilters({
  activeCategory,
  onCategoryChange,
  minRating,
  onMinRatingChange,
  verifiedOnly,
  onVerifiedChange,
  sortBy,
  onSortChange,
  onClearAll,
}: DirectoryFiltersProps) {
  const hasActiveFilters = activeCategory !== 'all' || minRating > 0 || verifiedOnly;

  return (
    <aside className="w-full lg:w-72 flex-shrink-0">
      <div className="sticky top-24 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-filter-3-line text-teal-600"></i>
            Filters
          </h3>
          {hasActiveFilters && (
            <button
              onClick={onClearAll}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Sort */}
        <div className="mb-6">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">
            Sort By
          </label>
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
          >
            <option value="featured">Featured First</option>
            <option value="rating">Highest Rated</option>
            <option value="reviews">Most Reviewed</option>
            <option value="name">A — Z</option>
            <option value="newest">Newest</option>
          </select>
        </div>

        {/* Categories */}
        <div className="mb-6">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 block">
            Category
          </label>
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
            {directoryCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => onCategoryChange(cat.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-teal-50 text-teal-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-base`}></i>
                </div>
                <span className="flex-1 text-left">{cat.name}</span>
                <span className={`text-xs ${activeCategory === cat.id ? 'text-teal-500' : 'text-gray-400'}`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Rating */}
        <div className="mb-6">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 block">
            Minimum Rating
          </label>
          <div className="space-y-1.5">
            {[0, 4, 4.5, 4.8].map((r) => (
              <button
                key={r}
                onClick={() => onMinRatingChange(r)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                  minRating === r
                    ? 'bg-teal-50 text-teal-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {r === 0 ? (
                  <span>All Ratings</span>
                ) : (
                  <>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < Math.floor(r) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'}`}
                        ></i>
                      ))}
                    </div>
                    <span>{r}+</span>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Toggles */}
        <div className="space-y-3 mb-6">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">
            Quick Filters
          </label>

          <button
            onClick={() => onVerifiedChange(!verifiedOnly)}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer border ${
              verifiedOnly
                ? 'bg-teal-50 border-teal-200 text-teal-700'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-verified-badge-line text-base"></i>
              </div>
              Verified Only
            </span>
            <div className={`w-9 h-5 rounded-full transition-colors relative ${verifiedOnly ? 'bg-teal-500' : 'bg-gray-300'}`}>
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${verifiedOnly ? 'translate-x-4' : 'translate-x-0.5'}`}></div>
            </div>
          </button>
        </div>

        {/* CTA */}
        <div className="pt-4 border-t border-gray-100">
          <a
            href="/advertise"
            className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
          >
            <i className="ri-megaphone-line"></i>
            List Your Business
          </a>
        </div>
      </div>
    </aside>
  );
}
